import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useConfirmationModalContext } from 'Hooks/modals/useConfirmationModal'
import { useCreateEmployerCountryQuery } from 'Hooks/api/JOONAdmin/useCreateEmployerCountryQuery'
import { CountryCode, CountryNames } from 'Utils/types/countries'
import { ChargeFeeType } from 'Utils/types/employerInfo'
import { useJoonAdminContext } from 'Contexts/JOONAdmin/JOONAdminContext'

export type CreateCountryFormValues = {
  countryCode: CountryCode
  processingFee: number
  pricingDescription: string
  alternativeReimbursement: boolean
  chargeFeePer: ChargeFeeType
}

export const useCreateCountryForm = (closeModal: () => void) => {
  const defaultValues: Partial<CreateCountryFormValues> = {
    processingFee: 0,
    pricingDescription: '',
    alternativeReimbursement: false,
    chargeFeePer: ChargeFeeType.Invite
  }

  const { control, getValues, watch, handleSubmit } = useForm<CreateCountryFormValues>({
    defaultValues
  })

  const {
    state: { selectedEmployer }
  } = useJoonAdminContext()

  const { mutateAsync: createEmployerCountryQuery } = useCreateEmployerCountryQuery()
  const { showModal } = useConfirmationModalContext()

  const handleSubmission: SubmitHandler<CreateCountryFormValues> = async (data) => {
    if (!selectedEmployer?.id) return

    const countryName = CountryNames[getValues('countryCode')]
    const result = await showModal(
      `Add Country: ${countryName}`,
      `Are you sure you want to add this country?`
    )

    if (result) {
      const response = await createEmployerCountryQuery({
        ...data,
        employerId: selectedEmployer.id
      })
      if (response.success) closeModal()
    }
  }

  const handleError: SubmitErrorHandler<CreateCountryFormValues> = (error) => {
    toast.error(
      error.processingFee?.message ??
        error.pricingDescription?.message ??
        error.countryCode?.message
    )
  }

  const registerOptions = {
    countryCode: { required: 'A country selection is required' }
  }

  return {
    control,
    registerOptions,
    watch,
    handleSubmit,
    handleSubmission,
    handleError
  }
}
